import { MediaItem } from './media-api';

/**
 * The order of the items of a size, kept the way the page works with it. The «full» order holds EVERY live item of the size, including the ones whose deletion
 * waits for its ten seconds (they are hidden, not gone: the server still has them). What the editor sees is the full order without the hidden ones.
 */

/** The ids of the items in the order the server has them (`position`, then `id`). The replacements are nested in their targets: they are not here. */
export function serverOrder(items: readonly MediaItem[]): number[] {
  return [...items].sort((a, b) => a.position - b.position || a.id - b.id).map(i => i.id);
}

export function sameOrder(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** An item moved from one place to another; the array itself is not changed. */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const out = [...list];
  if (from < 0 || from >= out.length || to < 0 || to >= out.length || from === to) {
    return out;
  }
  const [item] = out.splice(from, 1);
  out.splice(to, 0, item);
  return out;
}

/**
 * The order the page shows while it is not yet the server's: `desired` where the editor has put things, with what the server has that it does not know
 * (new uploads: at the end, in the server's order) and without what the server no longer has. `desired = null` = the server's order as it is.
 */
export function mergeOrder(desired: readonly number[] | null, server: readonly number[]): number[] {
  if (!desired) {
    return [...server];
  }
  const known = new Set(server);
  const kept = desired.filter(id => known.has(id));
  const have = new Set(kept);
  return [...kept, ...server.filter(id => !have.has(id))];
}

/** What the editor sees: the full order without the hidden items. */
export function visibleOrder(full: readonly number[], hidden: ReadonlySet<number>): number[] {
  return full.filter(id => !hidden.has(id));
}

/**
 * A visible item moved to another visible place. The hidden items keep their own places in the full order (the slots they take do not move), so that taking a
 * deletion back puts the item exactly where it was.
 */
export function reorderVisible(full: readonly number[], hidden: ReadonlySet<number>, fromVisible: number, toVisible: number): number[] {
  const visible = visibleOrder(full, hidden);
  const moved = moveItem(visible, fromVisible, toVisible);
  if (moved.every((id, i) => id === visible[i])) {
    return [...full];
  }
  let next = 0;
  return full.map(id => (hidden.has(id) ? id : moved[next++]));
}

/**
 * The ids to send in `PUT /order`: the server wants exactly its live items, so the hidden ones (not deleted yet) go at the end, in the order they had. `gone` are the ids
 * whose `DELETE` is already on its way: they are left out (the server may have dropped them by the time this arrives).
 */
export function wireOrder(full: readonly number[], hidden: ReadonlySet<number>, gone: ReadonlySet<number> = new Set()): number[] {
  return [...visibleOrder(full, hidden), ...full.filter(id => hidden.has(id))].filter(id => !gone.has(id));
}

/** Does the server have to be told? The ids it has (less the ones being deleted), against the ids that would be sent. */
export function orderNeedsSave(full: readonly number[], hidden: ReadonlySet<number>, server: readonly number[], gone: ReadonlySet<number> = new Set()): boolean {
  return !sameOrder(wireOrder(full, hidden, gone), server.filter(id => !gone.has(id)));
}
